import React from "react";
import { MultiShopSelectPlatformType, MultiShopSelectPlatformTypeName } from "@/components/MultiShopSelect/types";
import OrderDTO from "@/dto/order/OrderDTO";
import WaybillTemplateDTO from "@/dto/template/WaybillTemplateDTO";
import WaybillTemplateType from "@/enum/WaybillTemplateType";
import { AppointmentArrivalUrl } from "@/utils/Url";
import {
    WaybillTemplateRule,
    checkAppointmentArrivalOrderTemplateRule,
    checkEncryptedManualOrderTemplateRule,
    checkJdConsolidationOrderTemplateRule,
    checkOutOrderTemplateRule,
    checkShipBySfOrderTemplateRule,
    checkWaybillTemplateEcRule,
    checkWaybillTemplatePlatformRule,
} from "@/utils/WaybillTemplateRule";
import { Modal } from "antd";
import GlobalStore from "@/store";
import {
    checkDesignatedLogisticsCompany,
    checkDouDianPeriodPurchase,
    checkJdPetItem,
    checkManualOrderPrintDyEwaybill,
    checkPddWarehouse,
    checkTbShipBySfJc,
    checkTmallLogisticsUpgradeOrders,
    checkWxTemplateEwaybillAccountUsername,
    tbShipBySfJcConfirm,
} from "@/utils/printCheck";
import { ensureOrderItemPartQuantityOrdersShouldPrint } from "@/utils/OrderUtils";
import ShopType from "@/enum/ShopType";
import EwaybillAccountDTO from "@/dto/ewaybillAccount/EwaybillAccountDTO";
import { confirm } from "@/utils";
import { isOtherDropShippingSupplierPlatform } from "./otherDropShippingSupplier";

/** 校验模板和订单是否符合继续打印的条件 */
class PrintValidator {
    ewaybillAccount: EwaybillAccountDTO;

    constructor(
        public globalStore: typeof GlobalStore,
        public template: WaybillTemplateDTO,
        public orders: OrderDTO[],
        public platform: MultiShopSelectPlatformType,
    ) {
        this.ewaybillAccount = this.globalStore.ewaybillAccountStore.ewaybillAccountMapping.get(
            this.template.ewaybillShippingAddressId,
        );
    }

    /** 返回 'true' 表示可进行打印操作 */
    async validate() {
        try {
            if (!this.template) return false;

            const baseValidatePass = await this.validateBase();

            if (!baseValidatePass) return false;

            if (this.platform === MultiShopSelectPlatformType.tb) {
                return this.validateTbPlatform();
            } else if (this.platform === MultiShopSelectPlatformType.dy) {
                return this.validateDyPlatform();
            } else if (this.platform === MultiShopSelectPlatformType.ks) {
                return this.validateKwaiPlatform();
            } else if (this.platform === MultiShopSelectPlatformType.other) {
                return this.validateOtherPlatform();
            } else if (this.platform === MultiShopSelectPlatformType.pdd) {
                return this.validatePddPlatform();
            } else if (this.platform === MultiShopSelectPlatformType.wb) {
                return this.validateWbPlatform();
            } else if (this.platform === MultiShopSelectPlatformType.jd) {
                return this.validateJdPlatform();
            } else if (this.platform === MultiShopSelectPlatformType.channelShop) {
                return this.validateWxPlatform();
            } else if (isOtherDropShippingSupplierPlatform(this.platform)) {
                return this.validateOtherDropShippingSupplierPlatform();
            } else if (this.platform?.startsWith("ali")) {
                return this.validateAliPlatform();
            }

            return true;
        } catch (e) {
            console.error("error:", e);

            return false;
        }
    }

    async validateBase() {
        const shipBySfErrorMessage = checkShipBySfOrderTemplateRule(this.orders, this.template);
        if (shipBySfErrorMessage) {
            Modal.error({ title: "打印失败", content: shipBySfErrorMessage });

            return false;
        }

        /** 检验模板是否可以被当前订单所属的店铺使用 */
        if (this.template.allowedShopIds) {
            const notAllowShopIdSet = new Set<number>();

            this.orders?.forEach(({ shopId }) => {
                if (!notAllowShopIdSet.has(shopId)) {
                    const shop = this.globalStore.shopMapping.get(shopId);

                    const [, rule] = checkWaybillTemplateEcRule(shop, this.template);

                    if (rule === WaybillTemplateRule.NOT_ALLOWED_RULE) {
                        notAllowShopIdSet.add(shopId);
                    }
                }
            });

            if (notAllowShopIdSet.size !== 0) {
                const shopNames = [...notAllowShopIdSet]
                    .map((shopId) => this.globalStore.shopMapping.get(shopId)?.name)
                    .join();

                Modal.error({
                    title: "打印失败",
                    content: (
                        <p className="error">
                            模板『{this.template.name}』所属店铺设置了不允许『{shopNames}』使用该模板
                        </p>
                    ),
                });

                return false;
            }
        }

        try {
            await ensureOrderItemPartQuantityOrdersShouldPrint(this.orders, this.globalStore);
        } catch {
            return false;
        }

        /** 指定物流公司 */
        const needShowLogisticsCompanyCheckConfirm = checkDesignatedLogisticsCompany(
            this.orders,
            this.template.logisticsCompany,
        );

        if (needShowLogisticsCompanyCheckConfirm) {
            try {
                await confirm(
                    `所选的${
                        MultiShopSelectPlatformTypeName[this.platform]
                    }订单部分订单买家指定的物流公司和当前打印物流公司不一致，是否继续打印？`,
                );
            } catch {
                return false;
            }
        }

        return true;
    }

    /** 检验淘宝订单是否可以继续打印 */
    async validateTbPlatform() {
        if ([WaybillTemplateType.JDL, WaybillTemplateType.OFFICIAL].includes(this.template.type)) {
            await new Promise<boolean>((resolve) => {
                Modal.info({
                    title:
                        "平台为保护买家隐私数据泄露，已对订单进行加密。因此建议切换使用菜鸟电子面单打印，保证打印发货顺畅。",
                    onOk() {
                        resolve(true);
                    },
                    onCancel() {
                        resolve(true);
                    },
                    focusable: { focusTriggerAfterClose: true },
                    okCancel: false,
                    okText: "知道了",
                });
            });
        }

        /** 天猫物流升级弹窗确认 */
        await this.globalStore.ewaybillAccountStore.ensure();
        await checkTmallLogisticsUpgradeOrders(
            this.orders,
            this.template,
            this.globalStore.ewaybillAccountStore.ewaybillAccountMapping,
            this.globalStore.ewaybillAccountStore.rawEwaybillAccounts,
        );

        const taoTianShipBySfJcMessage = checkTbShipBySfJc(this.orders, this.template);

        if (taoTianShipBySfJcMessage) {
            await tbShipBySfJcConfirm();
        }

        return true;
    }

    /** 检验抖店订单是否可以继续打印 */
    async validateDyPlatform() {
        const appointmentArrivalErrorMessage = checkAppointmentArrivalOrderTemplateRule(this.orders, this.template);

        if (appointmentArrivalErrorMessage) {
            Modal.error({
                title: "打印失败",
                content: (
                    <div>
                        <p className="error">抖店平台要求承诺日达订单必须使用京东快递或者顺丰快递进行打印发货</p>
                        您可通过包含标签搜索所有承诺日达抖店进行批量打印，
                        <a href={AppointmentArrivalUrl} target="_blank">
                            点击查看教程
                        </a>
                    </div>
                ),
            });

            return false;
        }

        await this.globalStore.ewaybillAccountStore.ensure();

        // 抖音代发店铺必须自己店铺的电子面单模板
        const hasDyIopFlag = this.orders?.some((it) => {
            const shop = this.globalStore.shopMapping.get(it.shopId);

            if (shop?.type === ShopType.DOUDIAN_IOP) {
                const [, rule] = checkWaybillTemplatePlatformRule(this.ewaybillAccount, shop, this.template);

                if (rule === WaybillTemplateRule.DOUDIANIOP_RULE) {
                    return true;
                }
            }
        });

        if (hasDyIopFlag) {
            Modal.error({
                title: "提示",
                content: (
                    <div>
                        <span>抖店代发店铺订单只能用本店铺的电子面单账号模板，请切换模板使用，保证打印发货顺畅。</span>
                        <a href="https://qw15p3s8hv.feishu.cn/wiki/wikcnAcgClSfsTsSXO9WqU9uULZ" target="_blank">
                            点击这里查看开通抖店电子面单教程
                        </a>
                    </div>
                ),
            });

            return false;
        }

        const douDianPeriodPurchaseMessage = checkDouDianPeriodPurchase(this.orders);

        if (douDianPeriodPurchaseMessage) {
            Modal.error({
                title: "打印失败",
                content: (
                    <div>
                        <span>{douDianPeriodPurchaseMessage}</span>
                        <a
                            href="https://qw15p3s8hv.feishu.cn/wiki/TR7fwHPpxi85kZkED4gc3wbTnUe?from=from_copylink"
                            target="_blank"
                        >
                            查看周期购订单处理教程
                        </a>
                    </div>
                ),
            });

            return false;
        }

        return true;
    }

    /** 检验快手订单是否可以继续打印 */
    async validateKwaiPlatform() {
        await this.globalStore.ewaybillAccountStore.ensure();

        // 快手代发店铺必须自己店铺的电子面单模板
        const hasKsIopFlag = this.orders?.some((it) => {
            const shop = this.globalStore.shopMapping.get(it.shopId);

            if (shop?.type === ShopType.KWAI_DROPSHIP) {
                const [, rule] = checkWaybillTemplatePlatformRule(this.ewaybillAccount, shop, this.template);

                if (rule === WaybillTemplateRule.KWAI_DROPSHIP_RULE) {
                    return true;
                }
            }
        });

        if (hasKsIopFlag) {
            Modal.error({
                title: "提示",
                content: (
                    <div>
                        <span>快手代发店铺订单只能用本店铺的电子面单账号模板，请切换模板使用，保证打印发货顺畅。</span>
                        <a href="https://qw15p3s8hv.feishu.cn/wiki/wikcnE9yzP5xqiNNe6garhRMHqc" target="_blank">
                            点击这里查看开通快手电子面单教程
                        </a>
                    </div>
                ),
            });

            return false;
        }

        return true;
    }

    /** 检验其他平台订单是否可以继续打印 */
    async validateOtherPlatform() {
        await this.globalStore.ewaybillAccountStore.ensure();

        const encryptedErrorMessage = await checkEncryptedManualOrderTemplateRule(
            this.orders,
            this.template,
            this.ewaybillAccount,
        );

        if (encryptedErrorMessage) {
            Modal.error({ title: "打印失败", content: encryptedErrorMessage });

            return false;
        }

        await checkManualOrderPrintDyEwaybill(this.globalStore, this.orders, this.template, null);

        return true;
    }

    async validatePddPlatform() {
        await this.globalStore.ewaybillAccountStore.ensure();

        // 拼多多代发店铺必须自己店铺的电子面单模板
        const hasPddIopFlag = this.orders?.some((it) => {
            const shop = this.globalStore.shopMapping.get(it.shopId);

            if (shop?.type === ShopType.PDD_FDS) {
                const [, rule] = checkWaybillTemplatePlatformRule(this.ewaybillAccount, shop, this.template);

                if (rule === WaybillTemplateRule.PDD_FDS_RULE) {
                    return true;
                }
            }
        });

        if (hasPddIopFlag) {
            Modal.error({
                title: "提示",
                content: (
                    <div>
                        <span>
                            拼多多代发店铺订单只能用本店铺的电子面单账号模板，请切换模板使用，保证打印发货顺畅。
                        </span>
                        <a href="https://mms.pinduoduo.com/logistics/open" target="_blank">
                            点击这里查看开通拼多多电子面单教程
                        </a>
                    </div>
                ),
            });

            return false;
        }

        const warehouseMessage = checkPddWarehouse(this.orders);

        if (warehouseMessage) {
            Modal.error({ title: "打印失败", content: warehouseMessage });

            return false;
        }

        return true;
    }

    async validateWbPlatform() {
        const outOrderErrorMessage = checkOutOrderTemplateRule(this.orders, this.template, ShopType.WEBUY);

        if (outOrderErrorMessage) {
            Modal.error({ title: "打印失败", content: outOrderErrorMessage });

            return false;
        }

        return true;
    }

    async validateJdPlatform() {
        /**
         * 京东分区订单打印规则
         * 1. 京东供销订单必须使用京东电子面单模板，但是京东的订单可以使用快递官方的面单，这里需要特殊处理一下
         */

        /** 京东供销订单 */
        const jdPurchaseOrders: OrderDTO[] = [];
        /** 京东，京东供货商代发订单 */
        const notJdPurchaseOrders: OrderDTO[] = [];

        for (const order of this.orders) {
            const orderShop = this.globalStore.shopMapping.get(order.shopId);
            if (orderShop?.type === ShopType.JD_PURCHASE) {
                jdPurchaseOrders.push(order);
            } else {
                notJdPurchaseOrders.push(order);
            }
        }

        if (notJdPurchaseOrders.length > 0) {
            const outOrderErrorMessage = checkOutOrderTemplateRule(this.orders, this.template, ShopType.JD);

            if (outOrderErrorMessage) {
                Modal.error({ title: "打印失败", content: outOrderErrorMessage });

                return false;
            }

            const jdConsolidationMessage = checkJdConsolidationOrderTemplateRule(this.orders, this.template);

            if (jdConsolidationMessage) {
                Modal.error({ title: "打印失败", content: jdConsolidationMessage });

                return false;
            }
        }

        if (jdPurchaseOrders.length > 0) {
            for (const order of jdPurchaseOrders) {
                const shop = this.globalStore.shopMapping.get(order.shopId);

                const [pass] = checkWaybillTemplatePlatformRule(this.ewaybillAccount, shop, this.template);

                if (!pass) return false;
            }
        }

        const petItemMessage = checkJdPetItem(this.orders);

        if (petItemMessage) {
            Modal.error({ title: "打印失败", content: petItemMessage });

            return false;
        }

        return true;
    }

    async validateWxPlatform() {
        for (const order of this.orders) {
            const shop = this.globalStore.shopMapping.get(order.shopId);

            /** 有一个店铺弹出提示即可，不需要多次提示 */
            const checked = await checkWxTemplateEwaybillAccountUsername(this.globalStore, shop, this.template);

            if (checked) {
                break;
            }
        }

        return true;
    }

    async validateOtherDropShippingSupplierPlatform() {
        // if (this.platform === MultiShopSelectPlatformType.other_drop_shipping_supplier_ks) {
        //     return this.validateKwaiPlatform();
        // }

        // if (this.platform === MultiShopSelectPlatformType.other_drop_shipping_supplier_channel_shop) {
        //     return this.validateWxPlatform();
        // }

        // if (this.platform === MultiShopSelectPlatformType.other_drop_shipping_supplier_wb) {
        //     return this.validateWbPlatform();
        // }

        // if (this.platform === MultiShopSelectPlatformType.other_drop_shipping_supplier_other) {
        //     return this.validateOtherPlatform();
        // }

        return true;
    }

    async validateAliPlatform() {
        return true;
    }
}

export default PrintValidator;
